import type * as Member from "./types/member"
import { request } from "@/utils/service"
import { normalizePositiveShopId } from "@/utils/shopScope"

const PREFIX = "teashop_merchant/"

function emptyMemberLevelList() {
  return Promise.resolve({
    code: 0,
    msg: "",
    data: [],
  } as IApiResponseData<Member.MemberLevelInfoType[]>)
}

/** 通过手机号搜索会员 */
export function getMemberListByShopIdAndPhone(phone: string, shopId: number) {
  return request<Member.MemberListData>({
    url: `${PREFIX}merchant_customer_user/member_list_by_phone/${shopId}/${phone}`,
    method: "GET",
  })
}

/** 通过手机号搜索会员 */
export function getMemberDetailListByShopIdAndPhone(phone: string, shopId: number) {
  return request<Member.MemberDetailListData>({
    url: `${PREFIX}merchant_customer_user/member_detail_list_by_phone/${shopId}/${phone}`,
    method: "GET",
  })
}

/** 通过userId获取会员信息 */
export function getMemberInfoByUserId(userId: number) {
  return request<IApiResponseData<Member.MemberDetailInfoType>>({
    url: `${PREFIX}merchant_customer_user/get_by_user_id/${userId}`,
    method: "GET",
  })
}

/** 创建会员 */
export function createMember(data: Member.MemberDetailInfoTypeRequest) {
  // merchantCustomerUserId
  return request<IApiResponseData<number>>({
    url: `${PREFIX}merchant_customer_user/create`,
    method: "POST",
    data,
  })
}

/** 修改会员信息 */
export function updateMember(data: Member.MemberDetailInfoTypeRequest) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_customer_user`,
    method: "PUT",
    data,
  })
}

/** 获取会员列表 */
export function getMemberListBySearch(data: Member.MemberSearchInfoType) {
  return request<Member.MemberDetailInfoListData>({
    url: `${PREFIX}merchant_customer_user/list_by_search`,
    method: "POST",
    data,
  })
}

/** 按关键字查询会员信息手机号后四位 卡号 姓名 */
export function getMemberListByKeyword(params: Member.MemberInfoViewListQuery) {
  return request<Member.MemberInfoViewListData>({
    url: `${PREFIX}merchant_customer_user/search`,
    method: "GET",
    params,
    customHeaders: {
      'unmask': '1',
    },
  })
}

/** 获取会员等级列表 */
export function getMemberLevelListByShopId(shopId: number) {
  const normalizedShopId = normalizePositiveShopId(shopId)
  if (!normalizedShopId) return emptyMemberLevelList()
  return request<IApiResponseData<Member.MemberLevelInfoType[]>>({
    url: `${PREFIX}merchant_customer_user/level/list/${normalizedShopId}`,
    method: "GET",
  })
}

/** 获取会员等级列表 */
export function getExistMemberLevelListByShopId(shopId: number) {
  const normalizedShopId = normalizePositiveShopId(shopId)
  if (!normalizedShopId) return emptyMemberLevelList()
  return request<IApiResponseData<Member.MemberLevelInfoType[]>>({
    url: `${PREFIX}merchant/mms/memberLevel/existing/${normalizedShopId}`,
    method: "GET",
  })
}

/** 创建会员等级 */
export function createMemberLevel(data: Member.MemberLevelInfoType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/mms/memberLevel`,
    method: "POST",
    data,
  })
}

/** 修改会员等级 */
export function updateMemberLevel(data: Member.MemberLevelInfoType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/mms/memberLevel`,
    method: "PUT",
    data,
  })
}

/** 删除会员等级 */
export function deleteMemberLevel(levelId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/mms/memberLevel/${levelId}`,
    method: "DELETE",
  })
}

/** 通过id获取会员详情 */
export function getMemberInfoById(memberId: number) {
  return request<IApiResponseData<Member.MerchantCustomerUserDetailDTO>>({
    url: `${PREFIX}merchant_customer_user/detail/${memberId}`,
    method: "GET",
    customHeaders: {
      'unmask': '1',
    },
  })
}

/** 切换会员状态 */
export function switchMemberStatus(memberId: number, status: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_customer_user/switch_status/${memberId}/${status}`,
    method: "POST",
  })
}

/** 通过店铺id获取店铺充值规则 */
export function getRechargeRuleByShopId(shopId: number) {
  return request<IApiResponseData<Member.MemberRechargeRuleType[]>>({
    url: `${PREFIX}merchant/mms/memberTopUpPresentRule/${shopId}`,
    method: "GET",
  })
}

/** 获取会员充值规则列表 */
export function getRechargeRuleList(params?: PaginationRequest) {
  return request<IApiPageResponse<Member.MerchantMemberRechargeRuleView>>({
    url: `${PREFIX}merchant/mms/recharge/rule/list`,
    method: "GET",
    params,
  })
}

/** 创建店铺充值规则 */
export function createRechargeRule(data: Member.MemberRechargeRuleType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/mms/memberTopUpPresentRule`,
    method: "POST",
    data,
  })
}

/** 创建会员充值规则 */
export function createMmsRechargeRule(data: Member.CreateMerchantMemberRechargeRuleView) {
  return request<IApiResponseData<boolean | null>>({
    url: `${PREFIX}merchant/mms/recharge/rule`,
    method: "POST",
    data,
  })
}

/** 获取对应会员Id当前有效的充值规则 */
export function getEffectRechargeRule(params: { levelId: number }) {
  return request<IApiResponseData<Member.MemberRechargePresentDetail>>({
    url: `${PREFIX}/merchant_customer_user/effect/rule`,
    method: "GET",
    params,
  })
}

/** 获取对应时间内可用会员级别信息 */
export function getAvailableMemberLevelList(params: DateTimeRangeRequest) {
  return request<IApiResponseData<Member.AvailableLevel[]>>({
    url: `${PREFIX}merchant/mms/recharge/rule/level/available`,
    method: "GET",
    params,
  })
}

/** 修改店铺充值规则 */
export function updateRechargeRule(data: Member.MemberRechargeRuleType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/mms/memberTopUpPresentRule`,
    method: "PUT",
    data,
  })
}

/** 修改店铺充值规则 */
export function updateMmsRechargeRule(data: Member.CreateMerchantMemberRechargeRuleView) {
  return request<IApiResponseData<boolean | null>>({
    url: `${PREFIX}merchant/mms/recharge/rule`,
    method: "PUT",
    data,
  })
}

/** 删除店铺充值规则 */
export function deleteRechargeRule(ruleId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/mms/memberTopUpPresentRule/${ruleId}`,
    method: "DELETE",
  })
}

/** 删除会员充值规则 */
export function deleteMmsRechargeRule(ruleId: number) {
  return request<IApiResponseData<boolean | null>>({
    url: `${PREFIX}merchant/mms/recharge/rule/${ruleId}`,
    method: "DELETE",
  })
}

/** 批量停用/启用赠送规则 */
export function toggleActivateStatusBatch(data: Member.ActivateStatusBatchVOType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/mms/memberTopUpPresentRule/toggle_batch`,
    method: "PATCH",
    data,
  })
}

/** 更改规则是否启用 */
export function toggleRechargeRuleStatus(params: { ruleId: number; status: number }) {
  return request<IApiResponseData<boolean | null>>({
    url: `${PREFIX}merchant/mms/recharge/rule/rule/toggle`,
    method: "PUT",
    params,
  })
}

/** 创建会员充值记录 */
export function createMemberChargeRecord(data: Member.MerchantMemberChargeRecordVO) {
  return request<IApiResponseData<number>>({
    url: `${PREFIX}merchant_member_charge_record/create`,
    method: "POST",
    data,
  })
}

/** 获取商户会员充值记录 分页 */
export function getMemberChargeRecordList(params: Member.MemberChargeRecordListType) {
  return request<IApiPageResponse<Member.MemberChargeInfoView>>({
    url: `${PREFIX}merchant_member_charge_record/list`,
    method: "GET",
    params,
    customHeaders: {
      'unmask': '1',
    },
  })
}

/** 获取单个商户会员充值记录 */
export function getSingleMemberChargeRecordById(chargeRecordId: number) {
  return request<IApiResponseData<Member.MemberChargeInfoView>>({
    url: `${PREFIX}merchant_member_charge_record/single`,
    method: "GET",
    params: {
      chargeRecordId,
    },
  })
}

/** 通过会员充值记录ID查询单条数据 */
export function getMemberChargeRecordById(chargeRecordId: number) {
  return request<IApiResponseData<Member.MerchantMemberChargeRecord>>({
    url: `${PREFIX}merchant_member_charge_record/${chargeRecordId}`,
    method: "GET",
  })
}

/** 获取充值总览信息 */
export function getRechargeOverview(params: Member.MemberChargeRecordListType) {
  return request<IApiResponseData<Member.MemberChargeGeneralView>>({
    url: `${PREFIX}mms/stat/member/general`,
    method: "GET",
    params,
  })
}

/** 会员充值撤销 */
export function revokeMemberChargeRecord(data: { chargeRecordId: number; remark?: string }) {
  return request<IApiResponseData<boolean | null>>({
    url: `${PREFIX}merchant_member_charge_record/revoke`,
    method: "POST",
    data,
  })
}

/** 获取会员消费信息 */
export function consumptionStat(params: Member.MemberConsumptionQueryDTO) {
  return request<IApiPageResponse<Member.MemberConsumptionMergeTuple>>({
    url: `${PREFIX}mms/stat/consumption`,
    method: "GET",
    params,
    customHeaders: {
      'unmask': '1',
    },
  })
}

/** 获取会员消费总览信息 */
export function generalStat(params: Member.MemberConsumptionQueryDTO) {
  return request<IApiResponseData<Member.MemberConsumptionGeneralView>>({
    url: `${PREFIX}mms/stat/general`,
    method: "GET",
    params,
  })
}

/**
 * 搜索获取会员操作记录列表
 */
export function getMemberOperationRecordListBySearch(data: Member.MemberOperationRecordSearchInfoType) {
  return request<Member.MemberOperationRecordListData>({
    url: `${PREFIX}merchant_member_option_record/list`,
    method: "GET",
    params: data,
    customHeaders: {
      'unmask': '1',
    },
  })
}

/** 获取店铺会员统计数据 */
export function getMemberStatisticsByShopId(shopId: number) {
  return request<IApiResponseData<Member.MemberStatisticsType>>({
    url: `${PREFIX}merchant_customer_user/statistics/${shopId}`,
    method: "GET",
  })
}

/** 导入会员列表 */
export function importMemberList(data: Member.MemberInfoType[]) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_customer_user/import`,
    method: "POST",
    data,
  })
}

/** 发送会员消费验证码 */
export function sendMemberConsumeCaptcha(memberId: number) {
  return request<IApiResponseData<string>>({
    url: `${PREFIX}merchant_customer_user/captcha/send`,
    method: "POST",
    params: { memberId },
  })
}

/** 验证会员消费验证码 */
export function validateMemberConsumeCaptcha(id: string, captcha: string) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_customer_user/captcha/validate`,
    method: "POST",
    params: { id, captcha },
  })
}
